import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, AlertTriangle, XCircle } from "lucide-react";

interface ComplianceMetric {
  name: string;
  score: number;
  status: "excellent" | "good" | "warning" | "critical";
  details: string;
}

interface ComplianceScorecardProps {
  metrics: ComplianceMetric[];
  overallScore: number;
}

const statusConfig = {
  excellent: { icon: CheckCircle, className: "text-green-600", color: "bg-green-600" },
  good: { icon: CheckCircle, className: "text-blue-600", color: "bg-blue-600" },
  warning: { icon: AlertTriangle, className: "text-yellow-600", color: "bg-yellow-600" },
  critical: { icon: XCircle, className: "text-red-600", color: "bg-red-600" },
};

export function ComplianceScorecard({ metrics, overallScore }: ComplianceScorecardProps) {
  const overallStatus = 
    overallScore >= 95 ? "excellent" :
    overallScore >= 85 ? "good" :
    overallScore >= 70 ? "warning" : "critical";
  
  const OverallIcon = statusConfig[overallStatus].icon;
  
  return (
    <Card data-testid="card-compliance-scorecard">
      <CardHeader>
        <CardTitle>Compliance Scorecard</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground mb-2">Overall Compliance Score</div>
            <div className="flex items-center gap-3">
              <div className="text-4xl font-semibold">{overallScore}%</div>
              <OverallIcon className={`h-8 w-8 ${statusConfig[overallStatus].className}`} />
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          {metrics.map((metric, index) => {
            const StatusIcon = statusConfig[metric.status].icon;
            return (
              <div key={index} className="space-y-2" data-testid={`metric-${metric.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`h-4 w-4 ${statusConfig[metric.status].className}`} />
                    <span className="font-medium">{metric.name}</span>
                  </div>
                  <span className="font-semibold">{metric.score}%</span>
                </div>
                <Progress 
                  value={metric.score} 
                  className="h-2"
                />
                <p className="text-sm text-muted-foreground">{metric.details}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
